import {useMemo, useRef, useState} from 'react';
import _ from 'underscore';
import {
	type Column,
	type ColumnPinningState,
	type ColumnSizingState,
	type RowData,
	type Updater,
} from '@tanstack/react-table';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {useWhenChangedEffect} from '@netx/core-components/hooks/useWhenChangedEffect';

// get the index of the last column that has been resized by the user (ltr)
const getLastDirtiedColumnIndex = (
	columns: {id: string | number; getSize: () => number}[],
	dirtyColumns: Record<string, boolean>,
) =>
	Object.entries(dirtyColumns).reduce((acc, [key, value]) => {
		if (!value) return acc; // not dirty and shouldnt even be in the list
		const index = columns.findIndex((column) => column.id.toString() === key);
		return index > acc ? index : acc;
	}, -1);

export type UseColumnSizingParams<TData extends RowData> = {
	leftLeafColumnsWidth: number;
	rightLeafColumnsWidth: number;
	allLeafColumns: {id: string | number; getSize: () => number}[];
	rowWidth: number;
	isResizingColumn: boolean;
	stageWidth: number;
	columnPinning?: ColumnPinningState;
	onColumnSizingChange: (updater: Updater<ColumnSizingState>) => void;
	getTotalSize: () => number;
};

export const useColumnSizing = <TData extends RowData>({
	allLeafColumns,
	columnPinning,
	leftLeafColumnsWidth,
	rightLeafColumnsWidth,
	rowWidth,
	isResizingColumn,
	stageWidth,
	onColumnSizingChange: _onColumnSizingChange,
	getTotalSize,
}: UseColumnSizingParams<TData>) => {
	// store column manual sizing
	const [columnSizing, setColumnSizing] = useState<Record<string, number>>({});
	// track columns that have been manually resized by the user
	const dirtyColumnSizing = useRef<Record<string, boolean>>({});
	// flag to track if the column sizing was updated programmatically so we do not
	// add any columns to the dirty list that shouldn't be there
	const justUpdatedColumnSizingProgrammatically = useRef(false);

	const onColumnSizingChange = useConstCallback(
		(updaterOrValue: Updater<ColumnSizingState>) => {
			const old = {...columnSizing};
			const result =
				typeof updaterOrValue === 'function'
					? updaterOrValue(columnSizing)
					: updaterOrValue;

			if (!justUpdatedColumnSizingProgrammatically.current) {
				dirtyColumnSizing.current = Object.entries(result)
					.filter(
						([key, value]) =>
							old[key] !== value || dirtyColumnSizing.current[key],
					)
					.reduce((acc, [key]) => ({...acc, [key]: true}), {});
			}
			justUpdatedColumnSizingProgrammatically.current = false;
			setColumnSizing(result);
		},
	);

	const initialColumnWidths = useMemo(() => {
		setColumnSizing({});
		return allLeafColumns.reduce<Record<string, number>>(
			(acc, col) => ({...acc, [col.id]: col.getSize()}),
			{},
		);
	}, [allLeafColumns]);

	useWhenChangedEffect(
		[
			initialColumnWidths,
			leftLeafColumnsWidth,
			rightLeafColumnsWidth,
			rowWidth,
			isResizingColumn,
			stageWidth,
		],
		_.throttle(() => {
			if (isResizingColumn) return;
			const largestRowWidth = getTotalSize();
			if (largestRowWidth === stageWidth) return;

			const sizeDif = largestRowWidth / stageWidth;

			justUpdatedColumnSizingProgrammatically.current = true;
			// we will only update the column sizing for columns that are not pinned
			// we will not auto adjust anything the user has explicitely sized
			_onColumnSizingChange((prev) => {
				const lastDirtiedIndex = getLastDirtiedColumnIndex(
					allLeafColumns,
					dirtyColumnSizing.current,
				);
				const newState = allLeafColumns.reduce((acc, column, index) => {
					if (
						lastDirtiedIndex > index ||
						columnPinning?.left?.includes(column.id.toString()) ||
						columnPinning?.right?.includes(column.id.toString()) ||
						dirtyColumnSizing.current[column.id]
					)
						return acc;

					// TODO: cleanup
					// if we only have one unpinned column, make it the full width minus pinned column widths
					if (
						allLeafColumns.length -
							(columnPinning?.left?.length ?? 0) -
							(columnPinning?.right?.length ?? 0) ===
						1
					) {
						return {
							...acc,
							[column.id]: Math.max(
								stageWidth - leftLeafColumnsWidth - rightLeafColumnsWidth,
								initialColumnWidths[column.id],
							),
						};
					}

					return {
						...acc,
						[column.id]: Math.max(
							(prev[column.id] ?? initialColumnWidths[column.id]) / sizeDif,
							initialColumnWidths[column.id],
						),
					};
				}, prev);
				// console.log('newState', newState);
				return newState;
			});
		}, 16),
		{
			executeAtMount: true,
		},
	);

	return {
		columnSizing,
		setColumnSizing,
		onColumnSizingChange,
	};
};
